import {
  Inspection,
  InspectionMeta,
  InspectionBool,
  InspectionRequest,
  InspectionResponse,
} from '../types/Inspection'
import { useRef } from 'react'
import Axios, { AxiosError, AxiosResponse } from 'axios'
import useSwr from 'swr'
import api from '../axios'
import { InspectionB } from '../types/Billings'

// The { succeeded, message, data, meta } shape this endpoint returns.
// NB: InspectionResponse in types/Inspection.ts declares `'meta?'` with the
// question mark inside the quoted key, so it types a property literally named
// "meta?" rather than an optional `meta` — hence this local type.
interface InspectionEnvelope {
  data?: InspectionB[]
  meta?: InspectionMeta
}

export interface InspectionResult {
  data?: InspectionResponse
  error?: AxiosError
}

export const placeBooking = (booking: InspectionRequest, token?: string) => {
  const willPlaceBooking = new Promise<InspectionResult>((resolve) => {
    api.post('/api/Inspection/add-inspection', booking, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((respone: AxiosResponse<InspectionResponse>) => {
        resolve({ data: respone.data })
      })
      .catch((err: AxiosError) => {
        resolve({ error: err })
      })
  })

  return willPlaceBooking
}

export const useBookings = (token?: string) => {
  const fetcher = (url: string) =>
    api.get(url, { 
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((res: AxiosResponse<InspectionResponse>) => res.data.data)

  const { data, error, mutate } = useSwr(
    `/api/Inspection/get-inspection-by-id`,
    fetcher
  )
  return {
    bookings: data,
    isLoading: !error && !data,
    isError: error,
    // Exposed so callers can re-fetch on demand — e.g. when the Bookings tab
    // is opened. Without this the list only ever loads once, on mount.
    mutate,
  }
}


export const inspectionBool = async (
  request: InspectionBool, token: string
): Promise<any> => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
  const { data } = await api.post(
    `/api/Inspection/Accept-inspection`,
    request,
    config
  )
  return data
}

// Outgoing bookings — the ones this user placed. Kept on SWR like
// useBookings above so both lists cache and revalidate identically;
// previously this was a one-shot call, so the incoming list would refresh on
// window focus while this one silently went stale.
// This endpoint is paged server-side: it returns one page of rows plus a
// `meta` block ({ pageNumber, pageSize, totalRecords, totalPages, hasPrevious,
// hasNext }). Both are returned here — paginating client-side over a single
// page silently caps the list at pageSize and strands the rest.
export const OUTGOING_BOOKINGS_PAGE_SIZE = 20

export const useUserInspections = (
  token?: string,
  pageNumber: number = 1,
  pageSize: number = OUTGOING_BOOKINGS_PAGE_SIZE
) => {
  const fetcher = (url: string) =>
    api
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      // Keep the whole envelope: the rows alone don't say how many pages exist.
      .then((res: AxiosResponse<InspectionB[] | InspectionEnvelope>) => res.data)

  // The page is part of the SWR key, so changing it fetches that page.
  const { data, error, mutate, isValidating } = useSwr(
    `/api/Inspection/Get-Inspection-By-User?PageNumber=${pageNumber}&PageSize=${pageSize}`,
    fetcher
  )

  // Tolerate a bare array in case the endpoint ever returns one unwrapped.
  const rows = Array.isArray(data) ? data : data?.data
  const metaFromData = Array.isArray(data) ? undefined : data?.meta

  // Hold on to the last page we successfully loaded. Changing page changes the
  // SWR key, and a new key has no cached data, so `data` is undefined while the
  // request is in flight — which would empty the table for a beat and make it
  // vanish before the next page appears. (SWR's own keepPreviousData is v2
  // only; this project is on 1.x.)
  const previousRows = useRef<InspectionB[] | undefined>(undefined)
  const previousMeta = useRef<InspectionMeta | undefined>(undefined)

  if (rows !== undefined) {
    previousRows.current = rows
    previousMeta.current = metaFromData
  }

  return {
    outgoingBookings: rows ?? previousRows.current,
    meta: metaFromData ?? previousMeta.current,
    isLoading: !error && !data,
    // True while a page is being fetched — including when showing the
    // previous page's rows, so the UI can signal that it's updating.
    isValidating,
    isError: error,
    mutate,
  }
}

export const getUserInspections = async (
  token: string,
): Promise<any> => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await api.get(
      `/api/Inspection/Get-Inspection-By-User`,
      config,
    );
    return response;
  } catch (err) {
    // @ts-ignore
    let error: AxiosError<ValidationErrors> = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    return error.response.data;
  }
};